/**
 * The board's view selections, as data — deliberately free of React and of any import
 * that reaches it.
 *
 * The split from `useViewPrefs` is not decoration. This half is branchy parsing of
 * untrusted input and is what the tests exercise; keeping it React-free means those tests
 * need no DOM and run in milliseconds under the root runner, which globs the whole repo.
 * Component tests are the deliberate exception and are named `.tsx` — see
 * `vitest.config.ts`.
 */

/** The sentinel a filter carries when it is not filtering. Not a valid enum value. */
export const ALL = 'All';

/**
 * The tabs the board declares, in the order `App` renders them — ONE list, from which the
 * labels, the indices and the count are all derived.
 *
 * Everything about a tab is on its own line, which is the point: a second parallel list
 * would let a reorder desync the two silently, and `showsTickets` has to be stated rather
 * than defaulted, so a new tab cannot quietly inherit "the filters do not apply to me".
 *
 * ⚠️ `key` is the persistence contract, not just an identifier: it is what the stored
 * view preference holds. Reordering this list is free; renaming a key silently discards
 * that preference for anyone holding it, and the board reopens on the first tab.
 */
export const TABS = [
  { key: 'board', label: 'Board', showsTickets: true },
  { key: 'table', label: 'Table', showsTickets: true },
  // The same filtered list as Table, ordered by what changed last (DWLF-168).
  { key: 'recent', label: 'Recent', showsTickets: true },
  // Lists every epic from its own request; reads neither filter.
  { key: 'epics', label: 'Epics', showsTickets: false },
] as const;

export type TabKey = (typeof TABS)[number]['key'];

export const TAB: Record<TabKey, number> = Object.fromEntries(
  TABS.map((tab, index) => [tab.key, index]),
) as Record<TabKey, number>;

/**
 * The tab order as it was when the stored preference held an INDEX rather than a key.
 *
 * Entries written before DWLF-168 are numbers, and a number only means anything against
 * the order it was written against. Inserting Recent at position 2 shifted Epics from 2
 * to 3 — so without this, everyone with Epics remembered would have come back to a tab
 * they had never chosen, with nothing to suggest why.
 *
 * Frozen deliberately: it is a record of a past format, not a second copy of TABS, and it
 * must not be updated when tabs change again. Deleting it once no browser can still hold
 * a pre-DWLF-168 entry is DWLF-170 — nothing here observes when that is true.
 */
const LEGACY_TAB_ORDER: readonly string[] = ['board', 'table', 'epics'];

export const STORAGE_KEY = 'dwlf-tickets.view';

/**
 * Whether a tab renders the FILTERED ticket list.
 *
 * A filter changed while a tab that ignores it is showing is a filter nobody can see
 * applied — the same "recorded but not obeyed" failure as a tab click on an epic page.
 * An index that is not a tab shows nothing, so it does not show tickets.
 */
export const tabShowsTickets = (tab: number): boolean => TABS[tab]?.showsTickets ?? false;

export interface ViewPrefs {
  tab: number;
  epic: string;
  status: string;
}

/**
 * The stored tab, as an index into `TABS`, or 0 for anything this board does not offer.
 *
 * Stored as a KEY so that the tab ORDER is not load-bearing: the index is a position in a
 * list that gets reordered, and the reader is a browser that saved it days ago against a
 * different list. Numbers are still accepted, and read against the order they were
 * written against — see `LEGACY_TAB_ORDER`.
 */
function tabIndexOf(stored: unknown): number {
  const key =
    typeof stored === 'string'
      ? stored
      : typeof stored === 'number' && Number.isInteger(stored)
        ? LEGACY_TAB_ORDER[stored]
        : undefined;
  const index = TABS.findIndex((entry) => entry.key === key);
  return index === -1 ? 0 : index;
}

/**
 * Parse a stored entry, or null if there is nothing usable in it — the caller owns the
 * defaults, so a corrupt or hand-edited entry degrades to a fresh board, never a broken one.
 *
 * Values are validated rather than trusted: this is user-writable storage, and a `tab` of
 * 7 or an `epic` of `{}` would otherwise reach MUI and render an out-of-range control.
 */
export function parseViewPrefs(raw: string | null): ViewPrefs | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { tab, epic, status } = parsed as Record<string, unknown>;
  if (typeof epic !== 'string' || typeof status !== 'string') return null;
  return { tab: tabIndexOf(tab), epic, status };
}

export function readViewPrefs(): ViewPrefs | null {
  try {
    return parseViewPrefs(localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private windows and blocked site data throw on ACCESS, before any parsing.
    // Mirrors api.ts's getToken, which wraps the same call for the same reason.
    return null;
  }
}

export function writeViewPrefs(prefs: ViewPrefs): void {
  try {
    // The stored shape is NOT `ViewPrefs`: the tab is a key there and an index here.
    const tab = TABS[prefs.tab]?.key ?? TABS[0].key;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ tab, epic: prefs.epic, status: prefs.status }),
    );
  } catch {
    /* A preference that cannot be persisted still works for this tab. */
  }
}
